import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Star, StarOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface Spelling {
  id: string;
  word: string;
  meaning: string;
  pronunciation?: string;
  synonyms: string[];
  antonyms: string[];
  example_sentence?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  category: string;
  is_active: boolean;
  display_order: number;
}

interface SpellingOfTheDay {
  id: string;
  spelling_id: string;
  is_active: boolean;
  spelling?: Spelling;
}

const SpellingsManager = () => {
  const [spellings, setSpellings] = useState<Spelling[]>([]);
  const [spellingOfTheDay, setSpellingOfTheDay] = useState<SpellingOfTheDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSpelling, setEditingSpelling] = useState<Spelling | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [spellingToDelete, setSpellingToDelete] = useState<string | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    word: '',
    meaning: '',
    pronunciation: '',
    synonyms: '',
    antonyms: '',
    example_sentence: '',
    difficulty_level: 'medium',
    category: 'general',
    display_order: 0
  });

  useEffect(() => {
    fetchSpellings();
    fetchSpellingOfTheDay();
  }, []);

  const fetchSpellings = async () => {
    try {
      const { data, error } = await supabase
        .from('spellings')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setSpellings(data || []);
    } catch (error) {
      console.error('Error fetching spellings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch spellings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSpellingOfTheDay = async () => {
    try {
      const { data, error } = await supabase
        .from('spelling_of_the_day')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) {
        setSpellingOfTheDay(null);
        return;
      }
      const { data: spelling, error: spellingError } = await supabase
        .from('spellings')
        .select('*')
        .eq('id', data.spelling_id)
        .maybeSingle();
      if (spellingError) throw spellingError;
      setSpellingOfTheDay({ ...data, spelling } as any);
    } catch (error) {
      console.error('Error fetching spelling of the day:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const spellingData = {
        ...formData,
        synonyms: formData.synonyms.split(',').map(s => s.trim()).filter(s => s),
        antonyms: formData.antonyms.split(',').map(s => s.trim()).filter(s => s),
        display_order: Number(formData.display_order)
      };

      if (editingSpelling) {
        const { error } = await supabase
          .from('spellings')
          .update(spellingData)
          .eq('id', editingSpelling.id);
        
        if (error) throw error;
        toast({
          title: "Success",
          description: "Spelling updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('spellings')
          .insert([{ ...spellingData, is_active: true }]);
        
        if (error) throw error;
        toast({
          title: "Success",
          description: "Spelling created successfully",
        });
      }

      setIsFormOpen(false);
      setEditingSpelling(null);
      resetForm();
      fetchSpellings();
    } catch (error) {
      console.error('Error saving spelling:', error);
      toast({
        title: "Error",
        description: "Failed to save spelling",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (spelling: Spelling) => {
    setEditingSpelling(spelling);
    setFormData({
      word: spelling.word,
      meaning: spelling.meaning,
      pronunciation: spelling.pronunciation || '',
      synonyms: spelling.synonyms.join(', '),
      antonyms: spelling.antonyms.join(', '),
      example_sentence: spelling.example_sentence || '',
      difficulty_level: spelling.difficulty_level,
      category: spelling.category,
      display_order: spelling.display_order
    });
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!spellingToDelete) return;
    
    try {
      const { error } = await supabase
        .from('spellings')
        .delete()
        .eq('id', spellingToDelete);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Spelling deleted successfully",
      });
      
      fetchSpellings();
    } catch (error) {
      console.error('Error deleting spelling:', error);
      toast({
        title: "Error",
        description: "Failed to delete spelling",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSpellingToDelete(null);
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('spellings')
        .update({ is_active: !currentStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: `Spelling ${!currentStatus ? 'activated' : 'deactivated'}`,
      });
      
      fetchSpellings();
    } catch (error) {
      console.error('Error toggling spelling status:', error);
      toast({
        title: "Error",
        description: "Failed to update spelling status",
        variant: "destructive",
      });
    }
  };

  const setAsSpellingOfTheDay = async (spellingId: string) => {
    try {
      // First, deactivate current spelling of the day
      await supabase
        .from('spelling_of_the_day')
        .update({ is_active: false })
        .eq('is_active', true);

      // Then set the new one
      const { error } = await supabase
        .from('spelling_of_the_day')
        .insert([{ spelling_id: spellingId, is_active: true }]);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Spelling of the day updated",
      });
      
      fetchSpellingOfTheDay();
    } catch (error) {
      console.error('Error setting spelling of the day:', error);
      toast({
        title: "Error",
        description: "Failed to set spelling of the day",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      word: '',
      meaning: '',
      pronunciation: '',
      synonyms: '',
      antonyms: '',
      example_sentence: '',
      difficulty_level: 'medium',
      category: 'general',
      display_order: 0
    });
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div>Loading spellings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Spellings Management</h2>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Spelling
        </Button>
      </div>

      {/* Spelling of the Day Card */}
      {spellingOfTheDay && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Current Spelling of the Day
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">{spellingOfTheDay.spelling?.word}</h3>
                <p className="text-sm text-muted-foreground">{spellingOfTheDay.spelling?.meaning}</p>
              </div>
              <Badge className={getDifficultyColor(spellingOfTheDay.spelling?.difficulty_level || '')}>
                {spellingOfTheDay.spelling?.difficulty_level}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Spellings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {spellings.map((spelling) => (
          <Card key={spelling.id} className={`${!spelling.is_active ? 'opacity-50' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{spelling.word}</CardTitle>
                <div className="flex gap-1">
                  <Badge className={getDifficultyColor(spelling.difficulty_level)}>
                    {spelling.difficulty_level}
                  </Badge>
                  <Badge variant="secondary">{spelling.category}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">{spelling.meaning}</p>
              
              {spelling.synonyms.length > 0 && (
                <div className="mb-2">
                  <span className="text-xs font-medium text-green-700">Similar: </span>
                  <span className="text-xs text-muted-foreground">
                    {spelling.synonyms.slice(0, 3).join(', ')}
                  </span>
                </div>
              )}

              {spelling.antonyms.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-red-700">Opposite: </span>
                  <span className="text-xs text-muted-foreground">
                    {spelling.antonyms.slice(0, 3).join(', ')}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(spelling.id, spelling.is_active)}
                  >
                    {spelling.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAsSpellingOfTheDay(spelling.id)}
                    disabled={spellingOfTheDay?.spelling_id === spelling.id}
                  >
                    {spellingOfTheDay?.spelling_id === spelling.id ? 
                      <Star className="w-4 h-4 fill-current text-primary" /> : 
                      <StarOff className="w-4 h-4" />
                    }
                  </Button>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(spelling)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSpellingToDelete(spelling.id);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSpelling ? 'Edit Spelling' : 'Add New Spelling'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="word">Word *</Label>
                <Input
                  id="word"
                  value={formData.word}
                  onChange={(e) => setFormData({...formData, word: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="pronunciation">Pronunciation</Label>
                <Input
                  id="pronunciation"
                  value={formData.pronunciation}
                  onChange={(e) => setFormData({...formData, pronunciation: e.target.value})}
                  placeholder="e.g., /həˈloʊ/"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="meaning">Meaning *</Label>
              <Textarea
                id="meaning"
                value={formData.meaning}
                onChange={(e) => setFormData({...formData, meaning: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="example_sentence">Example Sentence</Label>
              <Textarea
                id="example_sentence"
                value={formData.example_sentence}
                onChange={(e) => setFormData({...formData, example_sentence: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="synonyms">Synonyms (comma separated)</Label>
                <Input
                  id="synonyms"
                  value={formData.synonyms}
                  onChange={(e) => setFormData({...formData, synonyms: e.target.value})}
                  placeholder="happy, joyful, cheerful"
                />
              </div>
              <div>
                <Label htmlFor="antonyms">Antonyms (comma separated)</Label>
                <Input
                  id="antonyms"
                  value={formData.antonyms}
                  onChange={(e) => setFormData({...formData, antonyms: e.target.value})}
                  placeholder="sad, unhappy, gloomy"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="difficulty_level">Difficulty Level</Label>
                <Select
                  value={formData.difficulty_level}
                  onValueChange={(value) => setFormData({...formData, difficulty_level: value as any})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: Number(e.target.value)})}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsFormOpen(false);
                setEditingSpelling(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingSpelling ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Spelling</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this spelling? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SpellingsManager;